/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectConfig.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";

import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import { CustomImportButton } from "../../../components";

import DenseViewBox from "../../../components/customComponentsV2/projectConfig/DenseViewBox";
import CompactViewBox from "../../../components/customComponentsV2/projectConfig/CompactViewBox";
import ViewProjectConfig from "./ViewProjectConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  adminSettingsAPI,
  HandleApiActions,
  profileActions,
  projectConfigAPI,
  settingsApis,
} from "../../../redux/actions";

import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import AddDialog from "../../../components/customComponentsV2/projectConfig/AddDialog";

import dayjs from "dayjs";
import CustomDatePicker from "../../../components/customComponentsV2/common/datepicker/CustomDatePicker";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";

import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import moment from "moment";
import InfoButton from "../../../components/infoButton/InfoButton";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import { showToastMessage } from "../../../redux/actions/actionHandler";
import CustomRadioButtonV3 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV3";
import CustomFormTextFieldV3 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomSelectV3 from "../../../components/customComponentsV2/common/select/CustomSelectV3";
import CustomDatePickerV2 from "../../../components/customComponentsV2/common/datepicker/CustomDatePickerV2";
import { GetSingleImages } from "../../../redux//actions/actionHandler";
import { ReactComponent as GridIcon } from "../../../assets/logoIcons/grid__l__stroke.svg";
import { ReactComponent as ListIcon } from "../../../assets/logoIcons/list__l__stroke.svg";
import CustomCheckboxMultiSelect from "../../../components/customComponentsV2/common/multiselect/CustomCheckboxMultiSelect";

import { toast } from "react-toastify";
import sortUp from "../../../assets/sortup.png";
import sortDown from "../../../assets/sortdown.png";

const mapUserOptions = (users = []) =>
  users.map((item) => ({
    label: item.name,
    subLabel: item.email,
    value: item?.user_id,
  }));

const buildZoneOptions = (projectZones = []) =>
  projectZones.map((data) => ({
    label: data?.zone_name,
    value: data?.position,
    sequence: data?.sequence,
  }));

const buildDeliveryMarketOptions = (markets = []) =>
  markets.map((market) => ({
    label: market,
    value: market,
  }));

const buildVehicleFunctionOptions = (items = []) =>
  items.map((item) => ({
    label: `${item?.name} (${item?.abbreviation})`,
    value: item?.id,
  }));

const buildPowertrainClassOptions = (items = []) =>
  items.map((item) => ({
    label: `${item?.name} (${item?.abbreviation})`,
    value: item?.id,
  }));

const buildManufacturerOptions = (items = []) =>
  items.map((item) => ({
    label: item?.name,
    subLabel: item?.address,
    value: item,
  }));

const buildRecreateProjectState = (
  parentProjectDetails,
  countryData,
  milestoneNameOptions,
) => {
  const parentZoneNameDetails = buildZoneOptions(
    parentProjectDetails?.project_zones || [],
  );

  const parentModelYear = parentProjectDetails?.model_year
    ? dayjs(`${parentProjectDetails?.model_year}-01-01`)
    : null;

  const parentCountryData = countryData.find(
    (item) =>
      item.country?.toLowerCase() ===
      parentProjectDetails?.country?.toLowerCase(),
  );

  const parentDeliveryMarketDetails = buildDeliveryMarketOptions(
    parentProjectDetails?.delivery_market || [],
  );

  const parentTargetMarket = parentProjectDetails?.target_market;

  const parentVehicleFunctionDetails = buildVehicleFunctionOptions(
    parentProjectDetails?.vehicle_function_details || [],
  );

  const parentPowerTrainClassDetails = buildPowertrainClassOptions(
    parentProjectDetails?.powertrain_class_details || [],
  );

  const parentMftDetails = buildManufacturerOptions(
    parentProjectDetails?.mft_details || [],
  );

  const parentOwnerDetails = parentProjectDetails?.owner_details?.[0]?.user_id
    ? mapUserOptions(parentProjectDetails?.owner_details)
    : [];

  const parentCoOwnerDetails = parentProjectDetails?.co_owner_details?.[0]
    ?.user_id
    ? mapUserOptions(parentProjectDetails?.co_owner_details)
    : [];

  const parentEditUserDetails = parentProjectDetails?.edit_user_details?.[0]
    ?.user_id
    ? mapUserOptions(parentProjectDetails?.edit_user_details)
    : [];

  const parentViewUserDetails = parentProjectDetails?.view_user_details
    ?.user_id
    ? mapUserOptions(parentProjectDetails?.view_user_details)
    : [];

  const parentMilestoneDetails = mapProjectMilestonesToRows(
    parentProjectDetails?.milestones,
    milestoneNameOptions,
  );

  return {
    projectName: parentProjectDetails?.name || "",
    projectStatus: parentProjectDetails?.status || "",
    modelYear: parentModelYear || null,
    trademark: parentProjectDetails?.trade_mark || "",
    vehiclePlatform: parentProjectDetails?.vehicle_platform || "",
    country: parentCountryData?.country || "",
    timeZone: parentProjectDetails?.region || "",
    architecture: parentProjectDetails?.architecture_name || "",
    numSeats: parentProjectDetails?.no_of_seats || "",
    zoneNames: parentZoneNameDetails,
    vehicleClass: parentProjectDetails?.veh_class,
    vehicleType: parentProjectDetails?.vehicle_type || "",
    targetMarket: parentTargetMarket,
    deliveryMarket: parentDeliveryMarketDetails || [],
    vehicleFunction: parentVehicleFunctionDetails || [],
    powertrainClass: parentPowerTrainClassDetails || [],
    manufacturerDetails: parentMftDetails || [],
    owner: parentOwnerDetails || [],
    coOwners: parentCoOwnerDetails || [],
    editors: parentEditUserDetails || [],
    viewers: parentViewUserDetails || [],
    milestones: parentMilestoneDetails,
  };
};

const applyRecreateProjectState = (setters, state) => {
  setters.setProjectName(state.projectName);
  setters.setProjectStatus(state.projectStatus);
  setters.setModelYear(state.modelYear);
  setters.setTrademark(state.trademark);
  setters.setVehiclePlatform(state.vehiclePlatform);
  setters.setCountry(state.country);
  setters.setTimeZone(state.timeZone);
  setters.setArchitecture(state.architecture);
  setters.setNumSeats(state.numSeats);
  setters.setZoneNames(state.zoneNames);
  setters.setVehicleClass(state.vehicleClass);
  setters.setVehicleType(state.vehicleType);
  setters.setTargetMarket(state.targetMarket);
  setters.setDeliveryMarket(state.deliveryMarket);
  setters.setVehicleFunction(state.vehicleFunction);
  setters.setPowertrainClass(state.powertrainClass);
  setters.setManufacturerDetails(state.manufacturerDetails);
  setters.setOwner(state.owner);
  setters.setCoOwners(state.coOwners);
  setters.setEditors(state.editors);
  setters.setViewers(state.viewers);
  setters.setMilestoneRows(state.milestones);
  setters.setMilestoneDraft(createMilestoneRow());
};

const createMilestoneRow = (overrides = {}) => ({
  id:
    (typeof crypto !== "undefined" &&
      crypto.randomUUID &&
      crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: "",
  milestoneDate: null,
  ...overrides,
});

const parseMilestoneDate = (milestoneDate) => {
  if (!milestoneDate) return null;
  const parsedDate = moment(milestoneDate, ["MM-YYYY", "YYYY-MM"], true);
  if (parsedDate.isValid()) return parsedDate.startOf("month");

  const fallbackDate = moment(milestoneDate);
  return fallbackDate.isValid() ? fallbackDate.startOf("month") : null;
};

const mapProjectMilestonesToRows = (
  milestones = [],
  milestoneNameOptions = [],
) => {
  if (!Array.isArray(milestones) || milestones.length === 0) {
    return [];
  }

  return milestones
    .map((milestone) => {
      const name = milestone?.name || "";
      const milestoneDate = parseMilestoneDate(milestone?.milestone_date);
      const selectedMilestoneOption = milestoneNameOptions.find(
        (option) => option.value === name,
      );

      return createMilestoneRow({
        name,
        milestoneDate,
        sequence: milestone?.sequence ?? selectedMilestoneOption?.sequence,
      });
    })
    .filter((milestone) => milestone.name && milestone.milestoneDate);
};

const getVehicleStatusList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

const milestoneDatePickerSx = {
  width: "160px !important",
  height: "250px !important",
  "& .MuiPickersLayout-root": {
    minWidth: "0 !important",
    maxWidth: "160px !important",
  },
  "& .MuiPickersLayout-contentWrapper": {
    padding: 0,
  },
  "& .MuiPickersCalendarHeader-switchViewButton": {
    display: "none",
  },
  "& .MuiPickersCalendarHeader-root": {
    position: "relative",
    paddingLeft: "1px",
    paddingRight: "1px",
    justifyContent: "space-between",
    minHeight: "30px",
    marginBottom: 0,
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  "& .MuiPickersCalendarHeader-label": {
    fontSize: "14px",
  },
  "& .MuiPickersArrowSwitcher-root": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  "& .MuiPickersArrowSwitcher-spacer": {
    display: "none",
  },
  "& .MuiPickersArrowSwitcher-button": {
    padding: 0,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
};

const buildProjectRequestPayload = ({
  projectName,
  currentProjectID,
  trademark,
  vehiclePlatform,
  projectStatus,
  architecture,
  manufacturerDetails,
  zoneNames,
  vehicleClass,
  powertrainClass,
  vehicleFunction,
  country,
  timeZone,
  deliveryMarket,
  coOwners,
  editors,
  viewers,
  targetMarket,
  vehicleType,
  numSeats,
  modelYear,
  milestones,
  includeEditorsViewers = false,
}) => ({
  name: projectName?.trim(),
  ...(currentProjectID ? { parent_project_id: currentProjectID } : null),
  ...(trademark?.length > 0 ? { trade_mark: trademark?.trim() || "" } : null),
  vehicle_platform: vehiclePlatform?.trim() || "",
  status: projectStatus || "",
  architecture_name: architecture || "",
  mft_details: manufacturerDetails?.map((data) => data?.value),
  ...(zoneNames?.length > 0 && architecture?.toLowerCase() === "zonal"
    ? {
      zones: zoneNames?.map((data) => ({
        zone_name: data?.label,
        position: data?.value,
        sequence: data?.sequence,
      })),
    }
    : {}),
  vehicle_class: parseInt(vehicleClass || ""),
  pwrtrn_class: powertrainClass?.map((str) => parseInt(str?.value)),
  ...(vehicleFunction?.length > 0
    ? { vehicle_funcs: vehicleFunction?.map((str) => parseInt(str?.value)) }
    : {}),
  current_time: moment().format(),
  country: country || "",
  region: timeZone || "",
  ...(deliveryMarket?.length > 0
    ? { delivery_market: deliveryMarket?.map((data) => data.value) }
    : {}),
  ...(coOwners?.length > 0
    ? { co_owners: coOwners?.map((data) => data?.value) }
    : null),
  ...(includeEditorsViewers && editors?.length > 0
    ? { edit_users: editors?.map((data) => data?.value) }
    : null),
  ...(includeEditorsViewers && viewers?.length > 0
    ? { view_users: viewers?.map((data) => data?.value) }
    : null),
  target_market: targetMarket || "",
  vehicle_type: vehicleType || "",
  no_of_seats: numSeats || "",
  model_year: modelYear?.$y,
  ...(milestones?.length > 0 ? { milestones } : null),
});

const buildFiltersParams = ({
  page_number,
  page_size,
  filtersPC,
  filterVehicleClass,
}) => {
  const isAll = filtersPC.all;

  return {
    page_number,
    page_size,
    order_by_sort: filtersPC.sortOrder,
    order_by_column: filtersPC.sortBy,
    status: isAll ? undefined : filtersPC.statusGroup.join(","),
    vehicle_class:
      isAll || filterVehicleClass.length === 0
        ? undefined
        : filterVehicleClass.map((d) => d.value).join(","),
    is_zonal:
      filtersPC.doubleGroup.CENTRALIZED === filtersPC.doubleGroup.ZONAL
        ? undefined
        : filtersPC.doubleGroup.ZONAL,
  };
};

function ProjectConfig() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profileDetails = useSelector((state) => state.profile);

  const atFilter = ["CENTRALIZED", "ZONAL"];

  //useStates
  const [openAddProject, setOpenAddProject] = useState(false);
  const [openViewMore, setOpenViewMore] = useState(false);
  const [selectCompactToDense, setSelectCompactToDense] = useState(false);

  // useStates for recreate project
  const [openRecreateProject, setOpenRecreateProject] = useState(false);
  const [getProjectTrigger, setGetProjectTrigger] = useState(false);

  //useState for Project Details
  const [allProjectDetails, setAllProjectDetails] = useState([]);

  const [pagePC, setPagePC] = useState(1);
  const [rowsPerPagePC, setRowsPerPagePC] = useState(25);

  // ✅ All form states
  const [projectName, setProjectName] = useState("");
  const [currentProjectID, setCurrentProjectID] = useState(null);
  const [projectStatus, setProjectStatus] = useState("");
  const [modelYear, setModelYear] = useState(null);
  const [trademark, setTrademark] = useState("");
  const [vehiclePlatform, setVehiclePlatform] = useState("");
  const [country, setCountry] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [architecture, setArchitecture] = useState("");
  const [numSeats, setNumSeats] = useState("");
  const [zoneNames, setZoneNames] = useState([]);
  const [vehicleClass, setVehicleClass] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [targetMarket, setTargetMarket] = useState("");
  const [deliveryMarket, setDeliveryMarket] = useState([]);
  const [vehicleFunction, setVehicleFunction] = useState([]);
  const [powertrainClass, setPowertrainClass] = useState([]);
  const [manufacturerDetails, setManufacturerDetails] = useState([]);
  const [owner, setOwner] = useState([]);
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [milestoneRows, setMilestoneRows] = useState([]);
  const [milestoneDraft, setMilestoneDraft] = useState(createMilestoneRow());

  //Data
  const [adminDefaultData, setAdminDefaultData] = useState({});
  const [countryData, setCountryData] = useState([]);
  const [powerTrainClassData, setPowertrainClassData] = useState([]);
  const [fullPowerTrainClassData, setFullPowerTrainClassData] = useState([]);
  const [vehicleFunctionData, setVehicleFunctionData] = useState([]);
  const [fullVehicleFunctionData, setFullVehicleFunctionData] = useState([]);
  const [vehicleClassData, setVehicleClassData] = useState([]);
  const [usersListData, setUsersListData] = useState([]);
  const [fullUsersList, setFullUsersList] = useState([]);
  const [milestoneNameOptions, setMilestoneNameOptions] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [image, setImage] = useState([]);

  //Filter and Sort
  const [anchorElPCFilter, setAnchorElPCFilter] = useState(null);
  const [openPCFilter, setOpenPCFilter] = useState(false);
  const [totalCountPC, setTotalCountPC] = useState(null);
  const [filtersPC, setFiltersPC] = useState({
    at_all: true,
    ps_all: true,
    doubleGroup: { CENTRALIZED: true, ZONAL: true },
    singleGroup: null,
    statusGroup: [100, 101, 200, 300, 500],
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [filtersPCAfterClick, setFiltersPCAfterClick] = useState({
    at_all: true,
    ps_all: true,
    doubleGroup: { CENTRALIZED: true, ZONAL: true },
    singleGroup: null,
    statusGroup: [100, 101, 200, 300, 500],
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [filterVehicleClass, setFilterVehicleClass] = useState([]);
  // useEffect(() => {
  //   if (filterVehicleClass.length > 0) {
  //     setFiltersPC((prev) => ({
  //       ...prev,
  //       at_all: true,
  //       ps_all: false,
  //       statusGroup: [100],
  //       doubleGroup: { CENTRALIZED: true, ZONAL: true },
  //     }));
  //   }
  // }, [filterVehicleClass]);

  console.log("filtersPCfiltersPCfiltersPC", filtersPC);

  const singleValueMap = {
    "ACTIVE PRE SOP": 100,
    "ACTIVE POST SOP": 200,
    DRAFT: 101,
    DEACTIVATED: 300,
    COMPLETED: 500,
  };
  const ALL_STATUS_VALUES = [100, 101, 200, 300, 500];
  const projectStatusConfig = {
    100: { label: "Active Pre SOP", color: "black", showInfo: true },
    101: { label: "Draft", color: "black", showInfo: true },
    200: { label: "Active Post SOP", color: "black", showInfo: true },
    300: { label: "Deactivated", color: "red", showInfo: false },
    500: { label: "Completed", color: "green", showInfo: true },
  };

  const getProjectStatusColor = (statusCode) => {
    switch (statusCode) {
      case 101:
        return "#007bff";
      case 100:
      case 200:
        return "#2ecc71";
      case 300:
        return "#808080";
      case 500:
        return "#000000";
      default:
        return "#000";
    }
  };

  const formatLabel = (str) => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) =>
        word === "sop" ? "SOP" : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join(" ");
  };

  const getProjectStatusView = (statusCode) => {
    console.log("---statusCode---", statusCode);
    const status = projectStatusConfig[statusCode];
    if (!status) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-end",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            textAlign: "end",
            color: getProjectStatusColor(statusCode),
          }}
        >
          {status.label}
        </Typography>
        <Box>
          {status.showInfo && (
            <InfoButton infoKey={"project_status"} color={"black"} />
          )}
        </Box>
      </Box>
    );
  };

  const handlePCFilterIconClick = (event) => {
    setAnchorElPCFilter(event.currentTarget);
    setOpenPCFilter(true);
  };

  const handlePCFilterIconClosePC = () => {
    setAnchorElPCFilter(null);
    setOpenPCFilter(false);
  };

  // const handleApplyFiltersPC = async (action) => {
  //   switch (action) {
  //     // 🔹 CLEAR ALL FILTERS
  //     case "discard":
  //       console.log("Filters Cleared");

  //       const ALL_STATUSES = [
  //         "CENTRALIZED",
  //         "ZONAL",
  //         "ACTIVE PRE SOP",
  //         "ACTIVE POST SOP",
  //         "DRAFT",
  //         "DEACTIVATED",
  //         "COMPLETED",
  //       ];

  //       // Reset all filter states (mark all as selected)
  //       setFiltersPC((prev) => ({
  //         ...prev,
  //         all: true,
  //         statusGroup: [100, 101, 200, 300, 500], // all ticked
  //         doubleGroup: {
  //           CENTRALIZED: true,
  //           ZONAL: true,
  //         },
  //       }));

  //       // Reset vehicle class multiselect
  //       setFilterVehicleClass([]);

  //       // Optionally reset page number
  //       setPagePC(1);

  //       // API Call to fetch unfiltered data
  //       try {
  //         const resp = await handleProjectDetails({
  //           page_number: 1,
  //           page_size: rowsPerPagePC,
  //         });

  //         setTotalCountPC(resp?.total_count);
  //         setAllProjectDetails(resp?.data || []);
  //       } catch (err) {
  //         console.error("❌ Error Fetching ALL Data:", err);
  //       }

  //       handlePCFilterIconClosePC(); // close drawer
  //       break;

  //     // 🔹 CANCEL — close without applying
  //     case "cancel":
  //       console.log("Cancelled — no change applied");
  //       handlePCFilterIconClosePC();
  //       break;

  //     // 🔹 APPLY — your logic runs
  //     case "apply":
  //       console.log("Applied Filters:", filtersPC);
  //       console.log("Applied Vehicle Class Filter", filterVehicleClass);

  //       try {
  //         const allSelected = filtersPC.all;

  //         const queryParam = {
  //           page_number: pagePC,
  //           page_size: rowsPerPagePC,
  //           order_by_sort: filtersPC.sortOrder,
  //           order_by_column: filtersPC.sortBy,

  //           status: allSelected ? undefined : filtersPC.statusGroup.join(","),
  //           vehicle_class: allSelected
  //             ? undefined
  //             : filterVehicleClass?.map((d) => d?.value).join(","),

  //           is_zonal:
  //             allSelected ||
  //             filtersPC.doubleGroup.CENTRALIZED === filtersPC.doubleGroup.ZONAL
  //               ? undefined
  //               : filtersPC.doubleGroup.ZONAL,
  //         };

  //         console.log("Final Query Params:", queryParam);

  //         const resp = await handleProjectDetails(queryParam);

  //         setTotalCountPC(resp?.total_count);
  //         setAllProjectDetails(resp?.data || []);
  //       } catch (err) {
  //         console.error("❌ Fetch error:", err);
  //       }

  //       handlePCFilterIconClosePC();
  //       break;

  //     default:
  //       console.warn("Unknown filter action: ", action);
  //   }

  //   console.log("filtersPC after action:", filtersPC);
  //   return;
  // };

  // Function to clear all filters

  const toggleStatus = (item, state) => {
    console.log("itemitem", item, state);
    setFiltersPC((prev) => {
      // CASE 1: User toggles ALL
      if (item === "ALL") {
        return {
          ...prev,
          ps_all: state,
          statusGroup: state ? ALL_STATUS_VALUES : [100],
        };
      }

      // CASE 2: User toggles individual status
      const value = singleValueMap[item];
      let updatedStatus = [...prev.statusGroup];

      if (state) {
        // ADD status
        if (!updatedStatus.includes(value)) {
          updatedStatus.push(value);
        }
      } else {
        // REMOVE status
        updatedStatus = updatedStatus.filter((v) => v !== value);
      }

      // Auto-select "100" if nothing is selected
      if (updatedStatus.length === 0) {
        updatedStatus = ["100"];
        return {
          ...prev,
          ps_all: state,
          statusGroup: state ? ALL_STATUS_VALUES : [100],
        };
      }

      // Determine if ALL should become true
      const isAllSelected = updatedStatus.length === ALL_STATUS_VALUES.length;

      return {
        ...prev,
        ps_all: isAllSelected,
        statusGroup: updatedStatus,
      };
    });
  };

  const toggleArchitecture = (item, state) => {
    console.log("---toggleArchitecture---", item, state);
    let updatedDoubleGroup;
    if (item === "ALL") {
      updatedDoubleGroup = {
        CENTRALIZED: state,
        ZONAL: state,
      };
    } else {
      updatedDoubleGroup = {
        CENTRALIZED:
          item === "CENTRALIZED" ? state : filtersPC.doubleGroup.CENTRALIZED,
        ZONAL: item === "ZONAL" ? state : filtersPC.doubleGroup.ZONAL,
      };
    }

    if (
      updatedDoubleGroup.CENTRALIZED == false &&
      updatedDoubleGroup.ZONAL == false
    ) {
      toast.error("At least one Architecture should be selected");
      return;
    }
    setFiltersPC((prev) => {
      // Update double group for single select

      console.log("---updatedDoubleGroup--", updatedDoubleGroup);
      // Compute at_all correctly
      const allState =
        updatedDoubleGroup.CENTRALIZED && updatedDoubleGroup.ZONAL;

      return {
        ...prev,
        at_all: allState,
        doubleGroup: updatedDoubleGroup,
      };
    });
  };

  const handleApplyFiltersPC = async (action) => {
    switch (action) {
      case "discard":
        setFiltersPC({
          at_all: true,
          ps_all: true,
          doubleGroup: { CENTRALIZED: true, ZONAL: true },
          statusGroup: ALL_STATUS_VALUES,
          sortBy: "created_at",
          sortOrder: "ASC",
        });

        setFilterVehicleClass([]);
        // setPagePC(1);

        // const resetResp = await handleProjectDetails({
        //   page_number: 1,
        //   page_size: rowsPerPagePC,
        // });

        // setTotalCountPC(resetResp?.total_count);
        // setAllProjectDetails(resetResp?.data || []);
        handlePCFilterIconClosePC();
        break;

      case "cancel":
        handlePCFilterIconClosePC();
        break;

      case "apply":
        const params = buildFiltersParams({
          page_number: pagePC,
          page_size: rowsPerPagePC,
          filtersPC,
          filterVehicleClass,
        });
        setFiltersPCAfterClick(params);

        // const resp = await handleProjectDetails(params);
        // setTotalCountPC(resp?.total_count);
        // setAllProjectDetails(resp?.data || []);
        handlePCFilterIconClosePC();
        break;
    }
  };

  const handleClearFilters = () => {
    setFiltersPC({
      sortBy: "Created at",
      sortOrder: "ASC",
    });
  };

  // const handleIntegrationFilterButton = (action) => {
  //   if (action === "apply") {
  //     applyFilters();
  //   } else if (action === "cancel") {
  //     handlePCFilterIconClose();
  //   } else if (action === "discard") {
  //     resetFilters();
  //   }
  // };

  const fetchInitialData = async () => {
    try {
      const [
        countryRegions,
        powertrainClass,
        vehicleFunction,
        adminSettings,
        vehicleClass,
        vehicleStatus,
        usersList,
        metaList,
      ] = await Promise.all([
        dispatch(HandleApiActions({ ...profileActions.get_coutry_regions })),
        dispatch(
          HandleApiActions({ ...adminSettingsAPI.get_powertrain_class }),
        ),
        dispatch(
          HandleApiActions({ ...adminSettingsAPI.get_vehicle_function }),
        ),
        dispatch(HandleApiActions({ ...adminSettingsAPI.get_admin_settings })),
        dispatch(HandleApiActions({ ...adminSettingsAPI.get_vehicle_class })),
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_vehicle_status,
            params: {
              page_number: 1,
              page_size: 1000,
            },
          }),
        ),
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_users_list,
            params: {
              page_size: 1000,
              page_number: 1,
            },
          }),
        ),
        // dispatch(HandleApiActions({ ...settingsApis.get_app_meta_data })),
      ]);

      console.log("✅ Data fetched:", {
        countryRegions,
        powertrainClass,
        vehicleFunction,
        adminSettings,
        vehicleClass,
        usersList,
        vehicleStatus,
      });
      setCountryData(countryRegions?.data || []);
      setAdminDefaultData(adminSettings || []);
      setPowertrainClassData(powertrainClass?.data || []);
      setFullPowerTrainClassData(powertrainClass?.data || []);
      setVehicleFunctionData(vehicleFunction?.data || []);
      setFullVehicleFunctionData(vehicleFunction?.data || []);
      setVehicleClassData(vehicleClass?.data || []);
      setUsersListData(usersList?.data || []);
      setFullUsersList(usersList?.data || []);
      setMilestoneNameOptions(
        getVehicleStatusList(vehicleStatus)
          .slice()
          .sort((a, b) => (a?.sequence || 0) - (b?.sequence || 0))
          .map((item) => ({
            label: item?.name,
            value: item?.name,
            sequence: item?.sequence,
          })),
      );
      // setMetaData(metaList?.data || []);
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [openAddProject, openRecreateProject]);

  // useEffect(() => {
  //   const fetchAllUsers = async () => {
  //     const resp = await dispatch(
  //       HandleApiActions({
  //         ...projectConfigAPI.get_users_list,
  //         params: {
  //           page_size: 1000,
  //           page_number: 1,
  //         },
  //       })
  //     );

  //     console.log("allUsers", resp);
  //     setFullUsersList(resp?.data)
  //   };

  //   console.log("FullUsersListFullUsersList",fullUsersList)

  //   fetchAllUsers();
  // }, []);

  //  const distinctUsers = (user_id) => {
  //   const selectUserDetails = fullUsersList?.find(
  //     (data) => data?.user_id === user_id
  //   );

  //   console.log("selectUserDetails", selectUserDetails);

  //   return selectUserDetails;
  // };
  //   console.log("distinctUsersdistinctUsers",distinctUsers("xE2Y1055j"))
  useEffect(() => {
    console.log("---form users---", coOwners, editors, viewers);
    console.log("---userData---", fullUsersList);

    if (!Array.isArray(fullUsersList)) return;

    // Get all selected user IDs
    const selectedUsers = [...coOwners, ...editors, ...viewers, ...owner].map(
      (user) => user.value,
    );

    // Filter unselected users
    const unSelectedUserListData = fullUsersList.filter(
      (user) => !selectedUsers.includes(user.user_id),
    );

    setUsersListData(unSelectedUserListData);

    console.log("---unSelectedUserListData---", unSelectedUserListData);
  }, [coOwners, editors, viewers, fullUsersList]);

  useEffect(() => {
    console.log(
      "--powerTrainClassData---",
      powertrainClass,
      fullPowerTrainClassData,
    );
    if (!Array.isArray(fullPowerTrainClassData)) return;

    const selectedPowerTrainClass = powertrainClass.map((pt) =>
      Number(pt.value),
    );

    const unselectedPowerTrainClass = fullPowerTrainClassData.filter(
      (pt) => !selectedPowerTrainClass.includes(Number(pt.id)),
    );

    console.log("---unselectedPowerTrainClass---", unselectedPowerTrainClass);

    setPowertrainClassData(unselectedPowerTrainClass);
  }, [powertrainClass, fullPowerTrainClassData]);

  useEffect(() => {
    console.log(
      "---full vehicle function data---",
      vehicleFunction,
      fullVehicleFunctionData,
    );

    if (!Array.isArray(fullVehicleFunctionData)) return;

    const selectedVf = vehicleFunction.map((pt) => Number(pt.value));

    const unselectedVf = fullVehicleFunctionData.filter(
      (pt) => !selectedVf.includes(Number(pt.id)),
    );

    console.log("---unselectedPowerTrainClass---", unselectedVf);

    setVehicleFunctionData(unselectedVf);
  }, [vehicleFunction, fullVehicleFunctionData]);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const metaList = await dispatch(
          HandleApiActions({ ...settingsApis.get_app_meta_data }),
        );

        setMetaData(metaList?.data || []);
      } catch (error) {
        console.error("Error fetching metadata:", error);
        setMetaData([]);
      }
    };

    fetchMetaData();
  }, []);

  console.log("Metajksahdchdadlad", metaData);

  useEffect(() => {
    if (country) {
      const selected = countryData?.find(
        (item) => item?.country?.toUpperCase() === country?.toUpperCase(),
      );
      if (selected) {
        setTimeZone(selected?.time_zone);
      }
    } else {
      setTimeZone("");
    }
  }, [country, countryData]);

  useEffect(() => {
    if (
      milestoneDraft.name &&
      milestoneRows.some((milestone) => milestone.name === milestoneDraft.name)
    ) {
      setMilestoneDraft((prev) => ({
        ...prev,
        name: "",
      }));
    }
  }, [milestoneDraft.name, milestoneRows]);

  const MilestoneSection = ({
    rows,
    draft,
    onAddMilestone,
    onRemoveMilestone,
  }) => {
    const [datePickerAnchor, setDatePickerAnchor] = useState(null);
    const [datePickerView, setDatePickerView] = useState("year");
    const [pendingMilestoneDate, setPendingMilestoneDate] = useState(null);

    const [activeMilestoneName, setActiveMilestoneName] = useState("");

    const milestoneDates = draft?.milestoneDates || {};

    const handleOpenDatePicker = (event, milestoneName) => {
      setDatePickerAnchor(event.currentTarget);
      setActiveMilestoneName(milestoneName);
      setPendingMilestoneDate(milestoneDates[milestoneName] || null);
      setDatePickerView("year");
    };

    const handleCloseDatePicker = () => {
      setDatePickerAnchor(null);
      setActiveMilestoneName("");
      setPendingMilestoneDate(null);
      setDatePickerView("year");
    };

    const updateMilestoneDate = (date) => {
      if (!activeMilestoneName) return;

      const selectedMilestoneOption = milestoneNameOptions.find(
        (option) => option.value === activeMilestoneName,
      );

      onAddMilestone([
        createMilestoneRow({
          name: activeMilestoneName,
          milestoneDate: date,
          sequence: selectedMilestoneOption?.sequence,
        }),
      ]);
    };

    const handleSelectedPickerClick = (event) => {
      const selectedButton = event.target.closest("button.Mui-selected");
      if (!selectedButton) return;

      const selectedText = selectedButton.textContent?.trim();
      const baseDate =
        pendingMilestoneDate || milestoneDates[activeMilestoneName] || moment();

      if (datePickerView === "year" && /^\d{4}$/.test(selectedText)) {
        setPendingMilestoneDate(
          baseDate.clone().year(Number(selectedText)).startOf("month"),
        );
        setDatePickerView("month");
        return;
      }

      if (datePickerView === "month") {
        const monthIndex = [...moment.monthsShort(), ...moment.months()]
          .findIndex(
            (month) => month.toLowerCase() === selectedText?.toLowerCase(),
          ) % 12;

        if (monthIndex === -1) return;

        updateMilestoneDate(baseDate.clone().month(monthIndex).startOf("month"));
        handleCloseDatePicker();
      }
    };

    const addedMilestoneNames = new Set(
      rows.map((milestone) => milestone.name?.trim()).filter(Boolean),
    );
    const availableMilestoneNameOptions = milestoneNameOptions.filter(
      (option) => !addedMilestoneNames.has(option.value),
    );
    const milestoneDropdownOptions =
      availableMilestoneNameOptions.length > 0
        ? availableMilestoneNameOptions
        : [{ label: "No data", value: "__no_data__" }];
    const getMilestoneSequence = (name) =>
      milestoneNameOptions.find((option) => option.value === name)?.sequence ??
      Number.MAX_SAFE_INTEGER;
    const sortedRows = rows
      .slice()
      .sort(
        (a, b) =>
          (a.sequence ?? getMilestoneSequence(a.name)) -
            (b.sequence ?? getMilestoneSequence(b.name)) ||
          a.name.localeCompare(b.name),
      );

    return (
      <Box
        sx={{
          width: "100%",
          // border: "1px solid #e0e0e0",
          // borderRadius: 0,
          // p: 2,
        }}
      >
        <Box
          sx={{
       
            mb: 2,
            overflowY:"auto",
            border: "2px solid #d9d9d9",
          }}
        >
          <Typography sx={{ fontWeight: 600 , mb: 1, p:1}}>Milestones</Typography>
        

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "end",
            p:1

          }}
        >
          {milestoneDropdownOptions.map((option) => (
            <React.Fragment key={option.value}>
              <CustomFormTextFieldV3
                label="Milestone Name"
                required
                value={option.value === "__no_data__" ? "" : option.value}
                placeholder="No data"
                disabled
              />
              <Box>
                <Typography
                  sx={{
                    mb: 0.5,
                    color: "#6f7174",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  }}
                >
                  Milestone Date <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="MM-YYYY"
                  value={milestoneDates[option.value]?.format("MM-YYYY") || ""}
                  onClick={(event) => {
                    if (option.value === "__no_data__") return;
                    handleOpenDatePicker(event, option.value);
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 18,
                          color: "#000",
                          cursor: "pointer",
                        }}
                      />
                    ),
                  }}
                  sx={{
                    cursor:
                      option.value === "__no_data__" ? "not-allowed" : "pointer",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: "#d9d9d9",
                        borderRadius: "0px",
                      },
                      "&:hover fieldset": { borderColor: "#8c8c8c" },
                      "&.Mui-focused fieldset": { borderColor: "#000" },
                    },
                  }}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>
        </Box>

        {rows.length > 0 ? (
          <Stack
            direction="row"
            mt={2}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              maxHeight: 72,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              pb: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bfbfbf",
                borderRadius: 10,
              },
            }}
          >
            {sortedRows.map((milestone) => (
              <Chip
                key={milestone.id}
                label={`${milestone.name} - ${milestone.milestoneDate?.format("MM-YYYY")}`}
                onDelete={() => onRemoveMilestone(milestone.id)}
                sx={{
                  backgroundColor: "#000",
                  color: "white",
                  borderRadius: 0,
                  px: 1.2,
                  py: 0.5,
                  mr: 1.2,
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                    fontSize: 18,
                    "&:hover": {
                      color: "white",
                    },
                  },
                }}
              />
            ))}
          </Stack>
        ) : null}

        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleCloseDatePicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { overflow: "visible", borderRadius: "0px" } }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box onClickCapture={handleSelectedPickerClick}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={
                  pendingMilestoneDate ||
                  milestoneDates[activeMilestoneName] ||
                  null
                }
                onChange={(newValue) => {
                  if (!newValue) return;
                  if (datePickerView === "year") {
                    setPendingMilestoneDate(newValue.startOf("month"));
                    setDatePickerView("month");
                    return;
                  }
                  updateMilestoneDate(newValue.startOf("month"));
                  handleCloseDatePicker();
                }}
                views={["year", "month"]}
                view={datePickerView}
                slotProps={{ actionBar: { actions: [] } }}
                sx={milestoneDatePickerSx}
              />
            </Box>
          </LocalizationProvider>
        </Popover>
      </Box>
    );
  };

  const componentMap = {
    CustomFormTextFieldV3,
    CustomSelectV2,
    CustomSelectV3,
    CustomMultiSelectV2,
    // CustomRadioButtonV2,
    CustomDatePicker,
    CustomDatePickerV2,
    CustomRadioButtonV3,
    MilestoneSection,
  };
  console.log("vehicleClassDatavehicleClassData", vehicleClassData);
  useEffect(() => {
    if (vehicleClassData && vehicleClassData.length) {
      const imageUrl = async () => {
        const fetchUrlArr = await Promise.all(
          vehicleClassData?.map(async (item) => {
            try {
              const response = await GetSingleImages({ image: item?.image });
              return { ...item, imageUrl: response };
            } catch (error) {
              return { ...item, imageUrl: "" };
            }
          }),
        );

        setImage(fetchUrlArr);
      };
      imageUrl();
    }
  }, [vehicleClassData]);

  const formLayout = [
    {
      component: "CustomFormTextFieldV3",
      props: {
        label: "Project Name",
        required: true,
        type: "text",
        fldType: "text",
        value: projectName,
        setValue: setProjectName,
        placeholder: "Enter Project Name",
        infoname: "name_of_the_project",
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        size: "small",
        infoname: "project_status",
        placeholder: "Select Project Status",
        label: "Project Status",
        required: true,
        handleOnChange: (e) => setProjectStatus(e.target.value),
        selectedValue: projectStatus,
        optionsList:
          Array.isArray(Object.entries(metaData?.project_status || {})) &&
          Object.entries(metaData?.project_status || {}).length > 0
            ? Object.entries(metaData?.project_status || {})
                .map(([key, value]) => ({
                  label: formatLabel(key.replace(/_/g, " ")),
                  value: value,
                }))
                .slice(0, 3)
            : [],
      },
    },
    {
      component: "CustomDatePickerV2",
      props: {
        label: "Select a Model Year",
        required: true,
        value: modelYear,
        onChange: (value) => {
          console.log("---model year---", value);
          setModelYear(value);
        },
      },
    },
    {
      component: "MilestoneSection",
      props: {
        rows: milestoneRows,
        draft: milestoneDraft,
        onAddMilestone: (milestonesToAdd = []) => {
          if (Array.isArray(milestonesToAdd) && milestonesToAdd.length > 0) {
            if (
              milestonesToAdd.some(
                (milestone) => !milestone.name || !milestone.milestoneDate,
              )
            ) {
              return;
            }

            setMilestoneRows((prev) => {
              const existingNames = new Set(prev.map((row) => row.name));

              return [
                ...prev,
                ...milestonesToAdd.filter(
                  (milestone) => !existingNames.has(milestone.name),
                ),
              ];
            });
            setMilestoneDraft(createMilestoneRow());
            return;
          }

          if (
            milestoneDraft.name?.trim().length < 3 ||
            !milestoneDraft.milestoneDate ||
            milestoneRows.some(
              (milestone) => milestone.name === milestoneDraft.name?.trim(),
            )
          ) {
            return;
          }

          const selectedMilestoneOption = milestoneNameOptions.find(
            (option) => option.value === milestoneDraft.name?.trim(),
          );

          setMilestoneRows((prev) => [
            ...prev,
            createMilestoneRow({
              name: milestoneDraft.name.trim(),
              milestoneDate: milestoneDraft.milestoneDate,
              sequence: selectedMilestoneOption?.sequence,
            }),
          ]);
          setMilestoneDraft(createMilestoneRow());
        },
        onRemoveMilestone: (id) =>
          setMilestoneRows((prev) => prev.filter((row) => row.id !== id)),
      },
    },
    {
      component: "CustomFormTextFieldV3",
      props: {
        label: "Trademark",
        required: false,
        value: trademark,
        setValue: setTrademark,
        placeholder: "Enter Trademark",
        infoname: "trademark",
      },
    },
    {
      component: "CustomFormTextFieldV3",
      props: {
        label: "Vehicle Platform",
        required: true,
        value: vehiclePlatform,
        setValue: setVehiclePlatform,
        placeholder: "Enter Vehicle Platform",
        infoname: "vehicle_platform",
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Country",
        label: "Country",
        required: true,
        handleOnChange: (e) => {
          console.log("select country", e.target.value);
          setCountry(e.target.value);
        },
        selectedValue: country,
        optionsList:
          Array.isArray(countryData) && countryData.length > 0
            ? countryData.map((item) => ({
                label: item?.country,
                value: item?.country,
              }))
            : [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Time Zone",
        label: "Time Zone",
        required: true,
        handleOnChange: (e) => setTimeZone(e.target.value),
        selectedValue: timeZone,
        optionsList:
          Array.isArray(countryData) && countryData.length > 0
            ? countryData.map((item) => ({
                label: item?.time_zone,
                value: item?.time_zone,
              }))
            : [],
      },
    },
    // {
    //   component: "CustomRadioButtonV2",
    //   props: {
    //     label: "Type of Architecture",
    //     required: true,
    //     data:
    //       Array.isArray(adminDefaultData?.data?.architecture_types) &&
    //       adminDefaultData?.data?.architecture_types.length > 0
    //         ? adminDefaultData?.data?.architecture_types.map((item) => ({
    //             label: item?.name,
    //             value: item?.name,
    //           }))
    //         : [],
    //     value: architecture,
    //     onChange: (event) => setArchitecture(event.target.value),
    //   },
    // },
    {
      component: "CustomRadioButtonV3",
      props: {
        label: "Type of Architecture",
        required: true,
        infoname: "type_of_architecture",
        data:
          adminDefaultData?.data?.architecture_types?.map((item) => ({
            label: item.name,
            value: item.name,
          })) || [],
        value: architecture,
        onChange: (e) => setArchitecture(e.target.value),
        direction: "column",
        gap: 1.5,
      },
    },
    {
      component: "CustomFormTextFieldV3",
      props: {
        label: "Number of Seats",
        fldType: "number",
        required: true,
        value: numSeats,
        setValue: (val) => {
          console.log("valvalvalvalval", val);
          const num = Number(val);
          if (num >= 1 && num <= 10) {
            setNumSeats(num);
          } else if (val === "") {
            setNumSeats("");
          }
        },
        placeholder: "Enter Number of Seats",
        type: "number",
        inputProps: {
          min: 1,
          max: 10,
        },
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        is_zonal: architecture === "ZONAL" ? true : false,
        label: "Zone Name",
        required: true,

        selectedValues: zoneNames,
        setSelectedValues: setZoneNames,
        optionsList:
          Array.isArray(adminDefaultData?.data?.zone_details) &&
          adminDefaultData?.data?.zone_details.length > 0
            ? adminDefaultData?.data?.zone_details.map((item) => ({
                label: item?.position_name,
                value: item?.zone_position,
                sequence: item?.sequence,
              }))
            : [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Vehicle Class",
        label: "Vehicle Class",
        infoname: "vehicle_class",
        required: true,
        handleOnChange: (e) => {
          console.log("Select Vehicle Class", e.target.value);
          setVehicleClass(e.target.value);
        },
        selectedValue: vehicleClass,
        optionsList:
          Array.isArray(image) && image?.length > 0
            ? image
                ?.map((item) => ({
                  name: item?.name,
                  class: item?.class,
                  sub_class: item?.sub_class,
                  label: `${item?.name} (${item?.class} - ${item?.sub_class})`,
                  value: item?.id,
                  image: item?.imageUrl,
                }))
                ?.sort((a, b) => {
                  if (a.class < b.class) return -1;
                  if (a.class > b.class) return 1;
                  if (a.sub_class < b.sub_class) return -1;
                  if (a.sub_class > b.sub_class) return 1;
                  return 0;
                })
            : [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Vehicle Type",
        label: "Vehicle Type",
        required: true,
        handleOnChange: (e) => setVehicleType(e.target.value),
        selectedValue: vehicleType,
        optionsList:
          Array.isArray(metaData?.vehicle_type) &&
          metaData?.vehicle_type.length > 0
            ? metaData?.vehicle_type.map((data) => ({
                value: data?.key,
                label: data?.value,
              }))
            : [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Target Market",
        label: "Target Market",
        infoname: "target_market",
        required: true,
        handleOnChange: (e) => setTargetMarket(e.target.value),
        selectedValue: targetMarket,
        optionsList:
          Array.isArray(adminDefaultData?.data?.product_delivery_markets) &&
          adminDefaultData?.data?.product_delivery_markets.length > 0
            ? adminDefaultData?.data?.product_delivery_markets.map((item) => ({
                label: item,
                value: item,
              }))
            : [],
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        placeholder: "Select Delivery Market",
        label: "Delivery Market",
        infoname: "delivery_market",
        required: false,
        selectedValues: deliveryMarket,
        setSelectedValues: setDeliveryMarket,
        optionsList:
          Array.isArray(adminDefaultData?.data?.product_delivery_markets) &&
          adminDefaultData?.data?.product_delivery_markets.length > 0
            ? adminDefaultData?.data?.product_delivery_markets.map((item) => ({
                label: item,
                value: item,
              }))
            : [],
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        label: "Vehicle Function",
        required: true,
        infoname: "vehicle_function",
        selectedValues: vehicleFunction,
        setSelectedValues: setVehicleFunction,
        optionsList:
          Array.isArray(vehicleFunctionData) && vehicleFunctionData.length > 0
            ? vehicleFunctionData.map((item) => ({
                label: `${item?.name} (${item?.abbreviation})`,
                value: item?.id,
              }))
            : [],
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        label: "Powertrain Class",
        required: true,
        selectedValues: powertrainClass,
        infoname: "power_train_class",
        setSelectedValues: setPowertrainClass,
        optionsList:
          Array.isArray(powerTrainClassData) && powerTrainClassData.length > 0
            ? powerTrainClassData.map((item) => ({
                label: `${item?.name} (${item?.abbreviation})`,
                value: item?.id,
              }))
            : [],
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        label: "Manufacturer Details",
        required: true,
        selectedValues: manufacturerDetails,
        infoname: "manufacturer_name",
        setSelectedValues: setManufacturerDetails,
        optionsList:
          Array.isArray(adminDefaultData?.data?.vehicle_manufacturers) &&
          adminDefaultData?.data?.vehicle_manufacturers.length > 0
            ? adminDefaultData?.data?.vehicle_manufacturers.map((item) => ({
                label: item?.name,
                subLabel: item?.address,
                value: item,
              }))
            : [],
        renderOption: (props, option) => (
          <li {...props} key={option.value}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {option.subLabel}
              </Typography>
            </Box>
          </li>
        ),
        getOptionLabel: (option) => option.label,
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        label: "Co Owners",
        required: false,
        selectedValues: coOwners,
        setSelectedValues: setCoOwners,
        optionsList:
          Array.isArray(usersListData) && usersListData.length > 0
            ? usersListData.map((item) => ({
                label: item.fullname,
                subLabel: item.email,
                value: item?.user_id,
              }))
            : [],
        renderOption: (props, option) => (
          <li {...props} key={option.value}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {option.subLabel}
              </Typography>
            </Box>
          </li>
        ),
        getOptionLabel: (option) => option.label,
      },
    },
    ...(openRecreateProject
      ? [
          {
            component: "CustomMultiSelectV2",
            props: {
              label: "Editor",
              required: false,
              selectedValues: editors,
              setSelectedValues: setEditors,
              optionsList:
                Array.isArray(usersListData) && usersListData.length > 0
                  ? usersListData.map((item) => ({
                      label: item.fullname,
                      subLabel: item.email,
                      value: item?.user_id,
                    }))
                  : [],
              renderOption: (props, option) => (
                <li {...props} key={option.value}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontWeight: 400 }}>
                      {option.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {option.subLabel}
                    </Typography>
                  </Box>
                </li>
              ),
              getOptionLabel: (option) => option.label,
            },
          },
          {
            component: "CustomMultiSelectV2",
            props: {
              label: "Viewer",
              required: false,
              selectedValues: viewers,
              setSelectedValues: setViewers,
              optionsList:
                Array.isArray(usersListData) && usersListData.length > 0
                  ? usersListData.map((item) => ({
                      label: item.fullname,
                      subLabel: item.email,
                      value: item?.user_id,
                    }))
                  : [],
              renderOption: (props, option) => (
                <li {...props} key={option.value}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontWeight: 400 }}>
                      {option.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {option.subLabel}
                    </Typography>
                  </Box>
                </li>
              ),
              getOptionLabel: (option) => option.label,
            },
          },
        ]
      : []),
  ];

  // ✅ Define which row indices (1-based) are single-column
  const singleColumnRows = [1, 3, 4, 5, 8, 9, 10, 11, 12, 13, 14, 15];

  //Redux
  const handleProjectDetails = async (params = {}) => {
    try {
      const {
        project_id,
        name,
        trade_mark,
        status,
        model_year,
        is_zonal,
        page_number,
        page_size,
        order_by_sort,
        order_by_column,
        vehicle_class,
      } = params;

      // Dynamically include only defined params
      const queryParams = {
        page_number,
        ...(page_size && { page_size }),
        ...(page_number && { page_number }),

        ...(project_id && { project_id }),
        ...(name && { name }),
        ...(trade_mark && { trade_mark }),
        ...(status && { status }),
        ...(vehicle_class && { vehicle_class }),
        ...(model_year && { model_year }),
        ...(is_zonal ? { is_zonal } : { is_zonal }),
        ...(order_by_sort && { order_by_sort }),
        ...(order_by_column && { order_by_column }),
      };

      console.log("queryParamsqueryParamsqueryParams", queryParams);

      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_project,
          params: queryParams,
        }),
      );

      return resp;
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  const startIndex = (pagePC - 1) * rowsPerPagePC;
  const endIndex = startIndex + rowsPerPagePC;
  const paginatedProjects = (
    Array.isArray(allProjectDetails) ? allProjectDetails : []
  ).slice(startIndex, endIndex);

  const fetchAllProjects = async () => {
    try {
      const resp = await handleProjectDetails({
        page_number: pagePC,
        page_size: rowsPerPagePC,
        order_by_sort: filtersPC.sortOrder,
        order_by_column: filtersPC.sortBy,
      });
      const allProjects = resp?.data || resp?.data || [];
      setTotalCountPC(resp?.total_count);
      setAllProjectDetails(allProjects);
    } catch (error) {
      console.error("❌ Error fetching all projects:", error);
    }
  };
  // useEffect(() => {
  //   fetchAllProjects();
  // }, [pagePC, rowsPerPagePC]);

  //Functions
  const handlePageChange = async (newPage) => {
    await setPagePC(newPage);
    const queryParam = buildFiltersParams({
      page_number: newPage,
      page_size: rowsPerPagePC,
      filtersPC,
      filterVehicleClass,
    });

    // const resp = await handleProjectDetails(queryParam);
    // setAllProjectDetails(resp?.data || []);
    // setTotalCountPC(resp?.total_count);
  };

  const handleOpenCloseAddProject = (value) => {
    setOpenAddProject(value);
    if (!value) {
      emptyStatePC();
    }
  };
  const handleOpenRecreateProject = async ({ project_id }) => {
    setCurrentProjectID(project_id);
    // api Call for parent Project details
    let resp = await dispatch(
      HandleApiActions({
        ...projectConfigAPI.get_project_recreate,
        params: { project_id: project_id },
        show_toast: false,
      }),
    );

    const parentProjectDetails = resp.data[0];
    console.log(
      "---parentCountryData---",
      // parentVehicleClassDetails,
      parentProjectDetails?.model_year,
    );
    if (!resp.error) {
      const recreateState = buildRecreateProjectState(
        parentProjectDetails,
        countryData,
        milestoneNameOptions,
      );
      applyRecreateProjectState(
        {
          setProjectName,
          setProjectStatus,
          setModelYear,
          setTrademark,
          setVehiclePlatform,
          setCountry,
          setTimeZone,
          setArchitecture,
          setNumSeats,
          setZoneNames,
          setVehicleClass,
          setVehicleType,
          setTargetMarket,
          setDeliveryMarket,
          setVehicleFunction,
          setPowertrainClass,
          setManufacturerDetails,
          setOwner,
          setCoOwners,
          setEditors,
          setViewers,
          setMilestoneRows,
          setMilestoneDraft,
        },
        recreateState,
      );
    }

    setOpenRecreateProject(true);
  };
  const handleCloseRecreateProject = (value) => {
    setOpenRecreateProject(value);
    if (!value) {
      emptyStatePC();
    }
  };
  const emptyStatePC = () => {
    setProjectName("");
    setCurrentProjectID(null);
    setProjectStatus("");
    setModelYear(null);
    setTrademark("");
    setVehiclePlatform("");
    setCountry("");
    setTimeZone("");
    setArchitecture("");
    setNumSeats("");
    setZoneNames([]);
    setVehicleClass("");
    setVehicleType("");
    setTargetMarket("");
    setDeliveryMarket([]);
    setVehicleFunction([]);
    setPowertrainClass([]);
    setManufacturerDetails([]);
    setCoOwners([]);
    setViewers([]);
    setEditors([]);
    setMilestoneRows([]);
    setMilestoneDraft(createMilestoneRow());
  };

  const handleOpenCloseViewMore = async (value, item = null) => {
    try {
      if (value && item) {
        navigate(`/dashboard/project-config?project_id=${item.project_id}`, {
          state: {
            projectId: item?.project_id,

            adminDefaultData: adminDefaultData,
            powerTrainClassData: powerTrainClassData,
            vehicleFunctionData: vehicleFunctionData,
            metaData: metaData,
          },
        });
      } else {
        navigate(`/dashboard/project-config`);
      }
    } catch (error) {
      console.error("❌ Error fetching project or variant details:", error);
    }
  };

  // useEffect(() => {
  //   console.log("hgfhsaGfksghfkjsahfhadfhakfjda");
  //   if (location.state?.refreshProjectId) {
  //     handleOpenCloseViewMore(true, location.state.refreshProjectId);
  //   }
  // }, [location.state?.refreshProjectId]);

  const buildMilestonesPayload = () =>
    milestoneRows
      .slice()
      .sort(
        (a, b) => {
          const getMilestoneSequence = (name) =>
            milestoneNameOptions.find((option) => option.value === name)
              ?.sequence ?? Number.MAX_SAFE_INTEGER;

          return (
            (a.sequence ?? getMilestoneSequence(a.name)) -
              (b.sequence ?? getMilestoneSequence(b.name)) ||
            a.name.localeCompare(b.name)
          );
        },
      )
      .map((milestone) => ({
        name: milestone.name?.trim(),
        milestone_date: milestone.milestoneDate?.format("MM-YYYY"),
        status: true,
      }))
      .filter((milestone) => milestone.name && milestone.milestone_date);

  const handleClickAddDialogProject = async () => {
    // const reqObj = {
    // project_name: projectName?.trim() || "",
    // project_status: projectStatus || "",
    // model_year: modelYear || "",
    // trademark: trademark?.trim() || "",
    // vehicle_platform: vehiclePlatform?.trim() || "",
    // country: country || "",
    // time_zone: timeZone || "",
    // architecture: architecture || "",
    // num_seats: numSeats || "",
    // zone_names: zoneNames || [],
    // vehicle_class: vehicleClass || "",
    // vehicle_type: vehicleType || "",
    // target_market: targetMarket || "",
    // delivery_market: deliveryMarket || [],
    // vehicle_function: vehicleFunction || [],
    // powertrain_class: powertrainClass?.map || [],
    // manufacturer_details: manufacturerDetails || [],
    // co_owners: coOwners || [],
    // };

    const reqObj = buildProjectRequestPayload({
      projectName,
      trademark,
      vehiclePlatform,
      projectStatus,
      architecture,
      manufacturerDetails,
      zoneNames,
      vehicleClass,
      powertrainClass,
      vehicleFunction,
      country,
      timeZone,
      deliveryMarket,
      coOwners,
      targetMarket,
      vehicleType,
      numSeats,
      modelYear,
      milestones: buildMilestonesPayload(),
      includeEditorsViewers: false,
    });

    console.log("reqObj...", reqObj);
    let resp = await dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_project,
        params: reqObj,
        show_toast: true,
      }),
    );
    if (!resp.error) {
      // dispatch(
      //   HandleApiActions({
      //     ...projectConfigAPI.get_project,
      //     params: {
      //       page_number: 1,
      //       page_size: Number.MAX_SAFE_INTEGER,
      //       // order_by_sort: selectedSortOrder,
      //       // order_by_column: selectedSortColumn,
      //     },
      //   })
      // );
      fetchAllProjects();
      handleOpenCloseAddProject(false);
    }

    console.log("🧾 Project Request Object:", reqObj);
  };

  const handleClickRecreateDialogProject = async () => {
    const reqObj = buildProjectRequestPayload({
      projectName,
      currentProjectID,
      trademark,
      vehiclePlatform,
      projectStatus,
      architecture,
      manufacturerDetails,
      zoneNames,
      vehicleClass,
      powertrainClass,
      vehicleFunction,
      country,
      timeZone,
      deliveryMarket,
      coOwners,
      editors,
      viewers,
      targetMarket,
      vehicleType,
      numSeats,
      modelYear,
      milestones: buildMilestonesPayload(),
      includeEditorsViewers: true,
    });

    console.log("reqObj...", reqObj);
    let resp = await dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_project_recreate,
        params: reqObj,
        show_toast: true,
      }),
    );
    if (!resp.error) {
      // fetchAllProjects();
      setGetProjectTrigger(!getProjectTrigger);
      handleCloseRecreateProject(false);
    }

    console.log("🧾 Project Request Object:", reqObj);
  };

  const isAddDisabledPC =
    projectName?.trim().length < 3 ||
    !projectStatus ||
    !modelYear ||
    architecture?.trim().length < 3 ||
    !vehicleClass ||
    !vehicleType ||
    country?.trim().length < 3 ||
    timeZone?.trim().length < 3 ||
    vehiclePlatform?.trim().length < 3 ||
    targetMarket?.trim().length < 3 ||
    !numSeats ||
    manufacturerDetails?.length === 0 ||
    powertrainClass?.length === 0 ||
    milestoneRows.length !== milestoneNameOptions.length;

  // const handleImportedJsonExcelFile = ({ fileType, importedFileData }) => {
  //   console.log("---importedFileData---", importedFileData);

  //   // Variables to store imported data
  //   let importedProjectDetails = {};

  //   if (!importedFileData) {
  //     showToastMessage({
  //       type: "error",
  //       message: "No data found in the imported file.",
  //     });
  //     return;
  //   }

  //   // showToastMessage({
  //   //   type: "info",
  //   //   message: `Importing ${fileType === "json" ? "JSON" : "Excel"} file...`,
  //   // });

  //   if (fileType === "json") {
  //     try {
  //       if (
  //         typeof importedFileData !== "object" ||
  //         Array.isArray(importedFileData)
  //       ) {
  //         throw new Error("Invalid JSON file format.");
  //       }

  //       // JSON format should match the export structure
  //       importedProjectDetails = importedFileData
  //         ? {
  //             name: importedFileData?.name || "",
  //             trade_mark: importedFileData?.trade_mark || "",
  //             vehicle_platform: importedFileData?.vehicle_platform || "",
  //             status: importedFileData?.status || "",
  //             architecture_name: importedFileData?.architecture_name || "",
  //             mft_details: importedFileData?.manufacture_details || [],
  //             ...(importedFileData?.zones
  //               ? { zones: importedFileData.zones }
  //               : {}),
  //             vehicle_class: importedFileData?.vehicle_class_details?.id || "",
  //             pwrtrn_class:
  //               importedFileData?.powertrain_class_details?.map(
  //                 (pr) => pr?.id,
  //               ) || [],
  //             vehicle_funcs:
  //               importedFileData?.vehicle_function_details?.map(
  //                 (vf) => vf?.id,
  //               ) || [],
  //             country: importedFileData?.country || "",
  //             region: importedFileData?.region || "",
  //             delivery_market: importedFileData?.delivery_market || [],
  //             target_market: importedFileData?.target_market || "",
  //             vehicle_type: importedFileData?.vehicle_type || "",
  //             no_of_seats: importedFileData?.no_of_seats || "",
  //             model_year: importedFileData?.model_year || "",
  //             co_owners:
  //               importedFileData?.co_owner_details?.map(
  //                 ({ user_id }) => user_id,
  //               ) || [],
  //             edit_users:
  //               importedFileData?.edit_user_details?.map(
  //                 ({ user_id }) => user_id,
  //               ) || [],
  //             view_users:
  //               importedFileData?.view_user_details?.map(
  //                 ({ user_id }) => user_id,
  //               ) || [],
  //           }
  //         : {};

  //       console.log(
  //         "---importedProjectDetails---",
  //         importedProjectDetails,
  //         Object.values(importedProjectDetails).filter(
  //           (pr) => pr !== "" && pr.length !== 0,
  //         ),
  //       );

  //       // customConsole("Formatted JSON Project Details Data: ");

  //       if (
  //         Object.values(importedProjectDetails).filter(
  //           (pr) => pr !== "" && pr.length !== 0,
  //         ).length <= 0
  //       ) {
  //         showToastMessage({
  //           type: "error",
  //           message: "Invalid JSON data",
  //         });
  //       }
  //     } catch (error) {
  //       showToastMessage({
  //         type: "error",
  //         message: error.message,
  //       });
  //     }
  //   } else if (fileType === "excel") {
  //     try {
  //       if (
  //         typeof importedFileData !== "object" ||
  //         Array.isArray(importedFileData)
  //       ) {
  //         throw new Error("Invalid Excel file format.");
  //       }

  //       Object.keys(importedFileData).forEach((sheetName) => {
  //         if (sheetName.toLowerCase() === "project_details") {
  //           if (importedFileData[sheetName]?.length > 0) {
  //             const pr = importedFileData[sheetName][0];
  //             importedProjectDetails = {
  //               name: pr?.name || "",
  //               trade_mark: pr?.trade_mark || "",
  //               vehicle_platform: pr?.vehicle_platform || "",
  //               status: pr?.status || "",
  //               architecture_name: pr?.architecture_name || "",
  //               mft_details: pr?.manufacture_details
  //                 ? JSON.parse(pr.manufacture_details)
  //                 : [],
  //               ...(pr?.zones ? { zones: JSON.parse(pr.zones) } : {}),
  //               vehicle_class: pr?.vehicle_class_details
  //                 ? JSON.parse(pr?.vehicle_class_details)?.id
  //                 : "",
  //               pwrtrn_class: pr?.powertrain_class_details
  //                 ? JSON.parse(pr?.powertrain_class_details || "[]")?.map(
  //                     (pr) => pr?.id,
  //                   )
  //                 : [],
  //               vehicle_funcs: pr?.vehicle_function_details
  //                 ? JSON.parse(pr?.vehicle_function_details || "[]")?.map(
  //                     (vf) => vf?.id,
  //                   )
  //                 : [],
  //               country: pr?.country || "",
  //               region: pr?.region || "",
  //               delivery_market: pr?.delivery_market
  //                 ? pr.delivery_market.split(",").map((item) => item.trim())
  //                 : [],
  //               target_market: pr?.target_market || "",
  //               vehicle_type: pr?.vehicle_type || "",
  //               no_of_seats: pr?.no_of_seats || "",
  //               model_year: pr?.model_year || "",
  //               co_owners: pr?.co_owner_details
  //                 ? JSON.parse(pr.co_owner_details || "[]")?.map(
  //                     (us) => us?.user_id,
  //                   )
  //                 : [],
  //               edit_users: pr?.edit_user_details
  //                 ? JSON.parse(pr.edit_user_details || "[]")?.map(
  //                     (us) => us?.user_id,
  //                   )
  //                 : [],
  //               view_users: pr?.view_user_details
  //                 ? JSON.parse(pr.view_user_details || "[]")?.map(
  //                     (us) => us?.user_id,
  //                   )
  //                 : [],
  //             };
  //           }
  //         }
  //       });
  //       if (Object.keys(importedProjectDetails).length === 0) {
  //         showToastMessage({
  //           type: "error",
  //           message: "Invalid Excel file format.",
  //         });
  //       }
  //     } catch (error) {
  //       showToastMessage({
  //         type: "error",
  //         message: error.message,
  //       });
  //     }
  //   } else {
  //     showToastMessage({
  //       type: "error",
  //       message: "Unsupported file type.",
  //     });
  //   }

  //   // handle import project details
  //   dispatch(
  //     HandleApiActions({
  //       ...projectConfigAPI.add_project,
  //       params: {
  //         ...importedProjectDetails,
  //         current_time: moment().format(),
  //       },
  //       show_toast: true,
  //     }),
  //   ).then(() => {
  //     // Get the profile details

  //     fetchAllProjects();
  //   });
  // };

  const isValidObject = (data) =>
    typeof data === "object" && !Array.isArray(data);

  const hasValidValues = (obj) =>
    Object.values(obj).filter((pr) => pr !== "" && pr.length !== 0).length > 0;

  // -------- JSON HANDLER --------
  const processJsonFile = (importedFileData) => {
    if (!isValidObject(importedFileData)) {
      throw new Error("Invalid JSON file format.");
    }

    const data = importedFileData;

    const formatted = {
      name: data?.name || "",
      trade_mark: data?.trade_mark || "",
      vehicle_platform: data?.vehicle_platform || "",
      status: data?.status || "",
      architecture_name: data?.architecture_name || "",
      mft_details: data?.manufacture_details || [],
      ...(data?.zones ? { zones: data.zones } : {}),
      vehicle_class: data?.vehicle_class_details?.id || "",
      pwrtrn_class: data?.powertrain_class_details?.map((pr) => pr?.id) || [],
      vehicle_funcs: data?.vehicle_function_details?.map((vf) => vf?.id) || [],
      country: data?.country || "",
      region: data?.region || "",
      delivery_market: data?.delivery_market || [],
      target_market: data?.target_market || "",
      vehicle_type: data?.vehicle_type || "",
      no_of_seats: data?.no_of_seats || "",
      model_year: data?.model_year || "",
      co_owners: data?.co_owner_details?.map(({ user_id }) => user_id) || [],
      edit_users: data?.edit_user_details?.map(({ user_id }) => user_id) || [],
      view_users: data?.view_user_details?.map(({ user_id }) => user_id) || [],
    };

    if (!hasValidValues(formatted)) {
      throw new Error("Invalid JSON data");
    }

    return formatted;
  };

  // -------- EXCEL HANDLER --------
  const processExcelFile = (importedFileData) => {
    if (!isValidObject(importedFileData)) {
      throw new Error("Invalid Excel file format.");
    }

    let formatted = {};

    Object.keys(importedFileData).forEach((sheetName) => {
      if (sheetName.toLowerCase() !== "project_details") return;

      const sheet = importedFileData[sheetName];
      if (!sheet?.length) return;

      const pr = sheet[0];

      formatted = {
        name: pr?.name || "",
        trade_mark: pr?.trade_mark || "",
        vehicle_platform: pr?.vehicle_platform || "",
        status: pr?.status || "",
        architecture_name: pr?.architecture_name || "",
        mft_details: pr?.manufacture_details
          ? JSON.parse(pr.manufacture_details)
          : [],
        ...(pr?.zones ? { zones: JSON.parse(pr.zones) } : {}),
        vehicle_class: pr?.vehicle_class_details
          ? JSON.parse(pr.vehicle_class_details)?.id
          : "",
        pwrtrn_class: pr?.powertrain_class_details
          ? JSON.parse(pr.powertrain_class_details || "[]").map((p) => p?.id)
          : [],
        vehicle_funcs: pr?.vehicle_function_details
          ? JSON.parse(pr.vehicle_function_details || "[]").map((vf) => vf?.id)
          : [],
        country: pr?.country || "",
        region: pr?.region || "",
        delivery_market: pr?.delivery_market
          ? pr.delivery_market.split(",").map((i) => i.trim())
          : [],
        target_market: pr?.target_market || "",
        vehicle_type: pr?.vehicle_type || "",
        no_of_seats: pr?.no_of_seats || "",
        model_year: pr?.model_year || "",
        co_owners: pr?.co_owner_details
          ? JSON.parse(pr.co_owner_details || "[]").map((u) => u?.user_id)
          : [],
        edit_users: pr?.edit_user_details
          ? JSON.parse(pr.edit_user_details || "[]").map((u) => u?.user_id)
          : [],
        view_users: pr?.view_user_details
          ? JSON.parse(pr.view_user_details || "[]").map((u) => u?.user_id)
          : [],
      };
    });

    if (Object.keys(formatted).length === 0) {
      throw new Error("Invalid Excel file format.");
    }

    return formatted;
  };

  // -------- MAIN FUNCTION --------
  const handleImportedJsonExcelFile = ({ fileType, importedFileData }) => {
    console.log("---importedFileData---", importedFileData);

    let importedProjectDetails = {};

    if (!importedFileData) {
      showToastMessage({
        type: "error",
        message: "No data found in the imported file.",
      });
      return;
    }

    try {
      if (fileType === "json") {
        importedProjectDetails = processJsonFile(importedFileData);
      } else if (fileType === "excel") {
        importedProjectDetails = processExcelFile(importedFileData);
      } else {
        throw new Error("Unsupported file type.");
      }
    } catch (error) {
      showToastMessage({
        type: "error",
        message: error.message,
      });
      return;
    }

    dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_project,
        params: {
          ...importedProjectDetails,
          current_time: moment().format(),
        },
        show_toast: true,
      }),
    ).then(() => {
      fetchAllProjects();
    });
  };

  console.log("fullUsersListfullUsersListfullUsersList", fullUsersList);

  return (
    <div>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.5,
          }}
        >
          {/* Left Section */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <CustomFilterV2
                anchorEl={anchorElPCFilter}
                open={openPCFilter}
                widthPerColumn="250px"
                handleClick={handlePCFilterIconClick}
                handleClose={handlePCFilterIconClosePC}
                handleFilterButton={handleApplyFiltersPC}
                showClearAll={true}
                handleClearAll={() => handleApplyFiltersPC("discard")}
                sections={[
                  // ======================= SORTING =======================
                  {
                    heading: "Sorting",
                    content: (
                      <Box>
                        <Box sx={{ p: 2 }}>
                          <CustomRadioButtonV2
                            value={filtersPC.sortBy}
                            onChange={(e) =>
                              setFiltersPC((prev) => ({
                                ...prev,
                                sortBy: e.target.value,
                              }))
                            }
                            data={[
                              { value: "created_at", label: "Created At" },
                              { value: "updated_at", label: "Updated At" },
                            ]}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",

                            gap: 0.5,
                          }}
                        >
                          {/* SORT ASC → DESC */}
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              setFiltersPC((prev) => ({
                                ...prev,
                                sortOrder: "ASC",
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setFiltersPC((prev) => ({
                                  ...prev,
                                  sortOrder: "ASC",
                                }));
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "pointer",
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 0,
                              backgroundColor:
                                filtersPC?.sortOrder === "ASC"
                                  ? "#26890D"
                                  : "transparent",
                              color:
                                filtersPC?.sortOrder === "ASC"
                                  ? "#FFFFFF"
                                  : "#000000",
                            }}
                          >
                            <img
                              src={sortUp}
                              alt="Sort Asc–Des"
                              style={{ width: 16, height: 16 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              Sort Asc – Des
                            </Typography>
                          </Box>

                          {/* SORT DESC → ASC */}
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              setFiltersPC((prev) => ({
                                ...prev,
                                sortOrder: "DESC",
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setFiltersPC((prev) => ({
                                  ...prev,
                                  sortOrder: "DESC",
                                }));
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "pointer",
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 0,
                              backgroundColor:
                                filtersPC?.sortOrder === "DESC"
                                  ? "#26890D"
                                  : "transparent",
                              color:
                                filtersPC?.sortOrder === "DESC"
                                  ? "#FFFFFF"
                                  : "#000000",
                            }}
                          >
                            <img
                              src={sortDown}
                              alt="Sort Des–Asc"
                              style={{ width: 16, height: 16 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              Sort Des – Asc
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ),
                  },

                  // =============== ARCHITECTURE TYPE (SINGLE SELECT) ===============
                  {
                    heading: "Filter By Architecture Type",
                    content: (
                      <Box
                        sx={{ display: "flex", flexDirection: "column", mt: 2 }}
                      >
                        {["ALL", ...atFilter].map((item) => {
                          console.log("---filtersPC---", filtersPC);
                          const checked =
                            (item === "ALL" && filtersPC.at_all) ||
                            (item === "CENTRALIZED" &&
                              filtersPC.doubleGroup.CENTRALIZED) ||
                            (item === "ZONAL" && filtersPC.doubleGroup.ZONAL);

                          return (
                            <CustomCheckBoxV2
                              key={item}
                              label={
                                item.charAt(0).toUpperCase() +
                                item.slice(1).toLowerCase()
                              }
                              value={checked}
                              onChange={(e) => {
                                const state = e.target.checked;
                                toggleArchitecture(item, state);
                              }}
                            />
                          );
                        })}
                      </Box>
                    ),
                  },

                  // ================= STATUS GROUP (SINGLE SELECT) =================
                  {
                    heading: "Filter By Project Status",
                    content: (
                      <Box
                        sx={{ display: "flex", flexDirection: "column", mt: 2 }}
                      >
                        {[
                          "ALL",
                          "ACTIVE PRE SOP",
                          "ACTIVE POST SOP",
                          "DRAFT",
                          "DEACTIVATED",
                          "COMPLETED",
                        ].map((item) => {
                          const value = singleValueMap[item];
                          const checked =
                            (item === "ALL" && filtersPC.ps_all) ||
                            filtersPC.statusGroup.includes(value);

                          return (
                            <CustomCheckBoxV2
                              key={item}
                              label={formatLabel(item)}
                              value={checked}
                              onChange={(e) => {
                                const state = e.target.checked;
                                toggleStatus(item, state);
                              }}
                            />
                          );
                        })}
                      </Box>
                    ),
                  },

                  // ================= VEHICLE CLASS MULTI SELECT =================
                  {
                    heading: "Vehicle Class",
                    content: (
                      <Box sx={{ width: "300px" }}>
                        <CustomCheckboxMultiSelect
                          required={false}
                          txtFldStyle={{ width: "250px" }}
                          selectedValues={filterVehicleClass}
                          setSelectedValues={setFilterVehicleClass}
                          optionsList={
                            Array.isArray(vehicleClassData) &&
                              vehicleClassData.length > 0
                              ? vehicleClassData.map((item) => ({
                                name: item.name,
                                class: item.class,
                                sub_class: item.sub_class,
                                label: `${item.name} (${item.class} - ${item.sub_class})`,
                                value: item.id,
                              }))
                              : []
                          }
                        />
                      </Box>
                    ),
                  },
                ]}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box>
                <CustomSearchbarWithApiV2
                  apiAction={{ ...projectConfigAPI.get_project }}
                  triggerApi={getProjectTrigger}
                  params={{
                    page_number: pagePC,
                    page_size: rowsPerPagePC,
                    order_by_sort: filtersPCAfterClick.sortOrder,
                    order_by_column: filtersPCAfterClick.sortBy,
                    // order_by_sort: "ASC",
                    // order_by_column: "created_at",
                  }}
                  searchKey="name"
                  size="small"
                  type="text"
                  variant="outlined"
                  placeholder="Search Projects"
                  valueError={""}
                  onSearchQueryChange={(newSearch, oldSearch) => {
                    console.log("----old vs new---", newSearch, oldSearch);
                    //   if (newSearch !== oldSearch) {
                    //     setPagePC(1)
                    //  }
                  }}
                  isMultiline={false}
                  minRows={1}
                  maxRows={3}
                  disabled={false}
                  txtFldStyle={{
                    width: "100%",
                  }}
                  getDataOnSearch={(data, resp) => {
                    console.log("Search datadata", data);
                    setTotalCountPC(resp?.total_count);
                    setAllProjectDetails(data);
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small"></IconButton>
                <Tooltip
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#000", // pure black tooltip
                        color: "black",
                        borderRadius: 0,
                        // boxShadow: 3,
                        p: 1.5,
                        "& .MuiTooltip-arrow": {
                          color: "#000",
                        },
                      },
                    },
                  }}
                  title={
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {/* Draft Project */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#fff", // white box
                            border: "1px solid #ccc",
                            borderRadius: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontSize: "0.85rem" }}
                        >
                          <b>Draft Project</b>
                        </Typography>
                      </Box>

                      {/* Completed Project */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#888", // grey box
                            borderRadius: "2px",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontSize: "0.85rem" }}
                        >
                          <b>Completed Project</b>
                        </Typography>
                      </Box>
                    </Box>
                  }
                >
                  {/* <IconButton size="small">
                    <InfoIcon style={{ color: "black" }} fontSize="small" />
                  </IconButton> */}
                </Tooltip>
              </Box>
            </Box>
            <Box
              sx={{ ml: -3 }}
              onClick={() => setSelectCompactToDense(!selectCompactToDense)}
            >
              {selectCompactToDense ? (
                <IconButton
                  size="small"
                  onClick={() => setSelectCompactToDense(false)}
                  sx={{
                    color: "black",
                    filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))",
                  }}
                >
                  <ListIcon />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => setSelectCompactToDense(true)}
                  sx={{
                    color: "black",
                    filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))",
                  }}
                >
                  <GridIcon />
                </IconButton>
              )}
            </Box>
            <Box sx={{ ml: -2 }}>
              <CustomImportButton
                handleImportedJsonExcelFile={handleImportedJsonExcelFile}
              />
            </Box>
          </Box>

          {/* Right Section */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* <Box>Filter Tags</Box> */}
            {/* <Box>
              <CustomButtonV2
                startIcon={<AddIcon />}
                type={"submit"}
                handleOnClick={() => handleOpenCloseAddProject(true)}
              />
            </Box> */}
            <Box>
              <AddButtonV2
                handleOnClick={() => handleOpenCloseAddProject(true)}
              />
            </Box>
          </Box>
        </Box>

        {/* Main */}
        <Box
          sx={{
            flexGrow: 1,
            p: 0.5,
            minHeight: "70vh",
            maxHeight: "75vh",
            overflowY: "auto",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-thumb": { borderRadius: "4px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            },
          }}
        >
          {selectCompactToDense ? (
            // 🔹 Dense Grid View
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
              }}
            >
              {allProjectDetails?.length > 0 ? (
                allProjectDetails.map((item, index) => (
                  <DenseViewBox
                    key={index}
                    onRecreateClick={() => {
                      handleOpenRecreateProject({
                        project_id: item.project_id,
                      });
                    }}
                    architectureName={item?.architecture_name}
                    imageUrl={item}
                    title={item?.name}
                    vehicleClass={`${item?.vehicle_class_details?.[0]?.name || ""
                      } - ${item?.vehicle_class_details?.[0]?.class || ""} - ${item?.vehicle_class_details?.[0]?.sub_class || ""
                      }`}
                    projectOwner={item?.owned_by?.name}
                    trademark={item?.trade_mark}
                    vehiclePlatform={item?.vehicle_platform}
                    createdAt={dayjs(item?.created_at).format(
                      "DD MMM YYYY, hh:mm A",
                    )}
                    updatedAt={dayjs(item?.updated_at).format(
                      "DD MMM YYYY, hh:mm A",
                    )}
                    status={getProjectStatusView(item.status)}
                    onViewMore={() => handleOpenCloseViewMore(true, item)}
                  />
                ))
              ) : (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <NoProjects />
                </Box>
              )}
            </Box>
          ) : (
            // 🔹 Compact View
            <>
              {allProjectDetails?.length > 0 ? (
                allProjectDetails.map((item, index) => (
                  <CompactViewBox
                    key={index}
                    onRecreateClick={() => {
                      handleOpenRecreateProject({
                        project_id: item.project_id,
                      });
                    }}
                    architectureName={item?.architecture_name}
                    imageUrl={item || {}}
                    title={item?.name}
                    vehicleClass={`${item?.vehicle_class_details?.[0]?.name || ""
                      } - ${item?.vehicle_class_details?.[0]?.class || ""} - ${item?.vehicle_class_details?.[0]?.sub_class || ""
                      }`}
                    projectOwner={item?.owned_by?.name}
                    trademark={item?.trade_mark}
                    vehiclePlatform={item?.vehicle_platform}
                    createdAt={dayjs(item?.created_at).format(
                      "DD MMM YYYY, hh:mm A",
                    )}
                    updatedAt={dayjs(item?.updated_at).format(
                      "DD MMM YYYY, hh:mm A",
                    )}
                    status={getProjectStatusView(item.status)}
                    onViewMore={() => handleOpenCloseViewMore(true, item)}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <NoProjects />
                </Box>
              )}
            </>
          )}
        </Box>
        {console.log("allProjectDetailsallProjectDetails", allProjectDetails)}

        {/* Footer */}
        {Array.isArray(allProjectDetails) && allProjectDetails.length > 0 ? (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              p: 2,

              zIndex: 999,
            }}
          >
            <CustomPaginationV2
              page={pagePC}
              rowsPerPage={rowsPerPagePC}
              totalItems={totalCountPC}
              onPageChange={handlePageChange}
            />
          </Box>
        ) : null}
      </Box>
      {openAddProject && (
        <AddDialog
          disabled={isAddDisabledPC}
          componentMap={componentMap}
          formLayout={formLayout}
          singleColumnRows={singleColumnRows}
          open={openAddProject}
          onClose={() => handleOpenCloseAddProject(false)}
          title={"Create Project"}
          handleClickAddDialog={handleClickAddDialogProject}
        />
      )}

      {openRecreateProject && (
        <AddDialog
          disabled={isAddDisabledPC}
          componentMap={componentMap}
          formLayout={formLayout}
          singleColumnRows={singleColumnRows}
          open={openRecreateProject}
          onClose={() => handleCloseRecreateProject(false)}
          title={"Recreate Project"}
          handleClickAddDialog={handleClickRecreateDialogProject}
        />
      )}
      {openViewMore && (
        <ViewProjectConfig
          onViewMore={() => handleOpenCloseViewMore(false, null)}
        />
      )}
    </div>
  );
}

export default ProjectConfig;

const NoProjects = () => (
  <Typography
    sx={{
      textAlign: "center",
      mt: 3,
      fontSize: 16,
      fontWeight: 500,
      color: "#7d7d7d",
    }}
  >
    No projects found
  </Typography>
);
